import _, { Dictionary } from 'lodash';
import { useCallback } from 'react';
import { INDIVIDUAL_WORKSPACES_CATEGORY, MAX_DIM } from 'spw-constants';
import { toFixedWithRounding } from 'spw-shared/utils';
import {
  IProjectSpaceType,
  ISpaceTypeCategory,
  ISpaceTypeSubcategory,
} from 'spw-redux/modules/space_types/types';
import {
  IProjectSpaceTypeCategory,
  IProjectSpaceTypeSubcategory,
} from 'spw-redux/modules/projects/types';
import { IChildWorksheetSpaceType } from 'spw-redux/modules/calculators/types';
import { isStorageSupportSpace } from 'spw-components/worksheet/utils';
import { isIWCategoryOrSubcategory } from 'spw-projects/utils';
import WorksheetStorageSpaceRow from '../../../../models/WorksheetStorageSpaceRow';
import WorksheetRow from '../../../../models/WorksheetRow';

export const useSpaceTypeInputValue = ({
  showDecimals: defaultShowDecimals,
  rowName,
  editFactor,
  editValue,
}: {
  showDecimals: boolean;
  rowName: string;
  editValue: string;
  editFactor: string;
}) => {
  const getSpaceTypeInputValue = useCallback(
    (
      fieldId: string,
      fieldValue: number,
      shouldReturnZeroWhenEmpty = true,
      showDecimalsForInput?: boolean
    ) => {
      const isFieldFocused = editFactor === `${rowName}.${fieldId}`;

      const showDecimals =
        showDecimalsForInput === undefined
          ? defaultShowDecimals
          : showDecimalsForInput;

      if (isFieldFocused) {
        const hasMoreThan2Decimals = editValue?.split('.')?.[1]?.length > 2;
        if (showDecimals)
          return hasMoreThan2Decimals
            ? toFixedWithRounding(Number(editValue), 2)
            : editValue;
        return editValue && Math.round(parseFloat(editValue)).toString();
      }
      if (!shouldReturnZeroWhenEmpty && !fieldValue) return '';
      return showDecimals
        ? toFixedWithRounding(Number(fieldValue), 2)
        : Math.round(fieldValue).toString();
    },
    [editFactor, editValue, rowName, defaultShowDecimals]
  );
  return { getSpaceTypeInputValue };
};

type RowSizeFields = 'sizeX' | 'sizeY';

export const useRowSizeInputs = ({
  editFactor,
  editValue,
  row,
  usfFieldKey = 'usf',
}: {
  editFactor: string;
  editValue: string;
  row: WorksheetRow | WorksheetStorageSpaceRow;
  usfFieldKey?: keyof WorksheetRow | keyof WorksheetStorageSpaceRow;
}) => {
  const onBlurSizeInput = useCallback(() => {
    let currentSizeField: RowSizeFields;
    let otherSizeField: RowSizeFields;
    if (editFactor.indexOf('sizeY') !== -1) {
      currentSizeField = 'sizeY';
      otherSizeField = 'sizeX';
    } else {
      currentSizeField = 'sizeX';
      otherSizeField = 'sizeY';
    }
    let size = _.isNaN(parseFloat(editValue))
      ? 0
      : _.min([parseFloat(editValue), MAX_DIM]);
    if (size < 0) {
      size = 0;
    }
    let newUsf =
      size === 0
        ? row[usfFieldKey as keyof WorksheetRow]
        : size * row[otherSizeField];
    if (!_.isFinite(newUsf)) {
      newUsf = 0;
    }
    let rowValues = {
      ...row,
      [currentSizeField]: size,
      [usfFieldKey]: newUsf,
    };
    if (size === 0) {
      rowValues = {
        ...rowValues,
        [otherSizeField]: 0,
      };
    }
    const changedRow = {
      ...row,
      ...rowValues,
    };
    return changedRow;
  }, [row, editValue, editFactor, usfFieldKey]);
  return {
    onBlurSizeInput,
  };
};

export const limitInputValue = (value: string, maxValue: number) =>
  parseFloat(value) <= maxValue || !value ? value : maxValue.toString();

export const getRowId = (row: WorksheetRow) =>
  `${row.child ? 'child-' : ''}${row.spaceType?.id}`;

export const getParentRowsWithNestedRows = (
  parentRows: WorksheetRow[],
  childRowsBySpaceType: Dictionary<WorksheetRow[]>
): WorksheetRow[] => {
  return parentRows.reduce((sortedRows, row: WorksheetRow) => {
    const childRows = childRowsBySpaceType[row.projectSpaceType.spaceType.id];

    if (childRows) {
      return [...sortedRows, row, ...childRows];
    }

    return [...sortedRows, row];
  }, []);
};

export const isWholeNumber = (number: number) =>
  Number((number || 0).toFixed(2)) % 1 === 0;

export const isRowDirty = (row: WorksheetRow) => {
  const rowFields = [
    'ratio',
    'quantity',
    'sizeX',
    'sizeY',
    'usf',
  ] as (keyof typeof row)[];

  return rowFields.some((field) => {
    return row[field] !== 0 && row[field] !== null;
  });
};

export const getCategoryOfSpaceType = (
  spaceType: IProjectSpaceType | IChildWorksheetSpaceType,
  spaceTypeCategories: IProjectSpaceTypeCategory[]
) => {
  const allCategories = spaceTypeCategories.reduce(
    (categories, spaceTypeCategory) => {
      if (spaceTypeCategory.subcategories) {
        return categories.concat(spaceTypeCategory.subcategories);
      }

      return [...categories, spaceTypeCategory];
    },
    []
  );

  return (spaceType as IProjectSpaceType).category
    ? (spaceType as IProjectSpaceType).category
    : allCategories.find(
        (stc) =>
          stc.defaultSpaceTypeCategory.category ===
          (spaceType as IChildWorksheetSpaceType).parent.spaceTypeCategory
            .category
      );
};

export const getDefaultCategory = (
  spaceTypeCategory: IProjectSpaceTypeCategory | IProjectSpaceTypeSubcategory,
  defaultCategories: ISpaceTypeCategory[]
) => {
  return isIWCategoryOrSubcategory(spaceTypeCategory)
    ? defaultCategories.find(
        (stc: ISpaceTypeCategory) =>
          stc.category === INDIVIDUAL_WORKSPACES_CATEGORY
      )
    : (spaceTypeCategory.defaultSpaceTypeCategory as ISpaceTypeCategory);
};
export const defaultCategoryAndSubcategory = (
  spaceTypeCategory: IProjectSpaceTypeCategory | IProjectSpaceTypeSubcategory,
  spaceTypeCategories: (
    | IProjectSpaceTypeCategory
    | IProjectSpaceTypeSubcategory
  )[]
) => {
  let category: ISpaceTypeCategory;
  let subcategory: ISpaceTypeSubcategory;

  if (spaceTypeCategory?.parentId) {
    const parentCategory = spaceTypeCategories.find(
      (stc) => stc.id === spaceTypeCategory.parentId
    );
    category = parentCategory.defaultSpaceTypeCategory;
    subcategory = spaceTypeCategory.defaultSpaceTypeCategory as ISpaceTypeSubcategory;
  } else {
    category = spaceTypeCategory?.defaultSpaceTypeCategory;
  }

  return { category, subcategory };
};

export const getPreviousId = (
  worksheetRows: WorksheetRow[],
  category: IProjectSpaceTypeCategory | IProjectSpaceTypeSubcategory
) => {
  let previousId = null;
  const categoryRows = worksheetRows.filter(
    (r) =>
      (r.projectSpaceTypeCategory.id === category.id ||
        r.projectSpaceTypeSubcategory?.id === category.id) &&
      !r.child &&
      !isStorageSupportSpace(r.spaceType)
  );
  if (_.some(categoryRows, (r) => r?.projectSpaceType?.previousId)) {
    // if so, then concat this new custom space to the end of the list
    previousId = categoryRows[categoryRows.length - 1].projectSpaceType.id;
  }
  return previousId;
};
